import { useState } from 'react';

import clsx from 'clsx';

// Hooks
import { useToastsContext } from '@/utils/hooks/context/toast/useToastsContext';
import useToastsNotifications from '@/utils/hooks/swr/notifications/useToastsNotifications';
// Types
import type { mutateNotificationsType } from '@/utils/hooks/swr/useNotifications';
import useTranslation from '@/utils/hooks/translation/useTranslation';

// Client
import { post } from '@/utils/client';

import type { NotificationIdType } from '@/utils/hooks/context/toast/types';

// Lib
import { NOTIFICATIONS_MARK_AS_READ } from '@/lib/routes/apiEndpoints';

// Components
import Icon from '@/components/base/Icon';
import Loader from '@/components/base/Loader';

type ButtonMarkAsReadProp = {
    mutateNotifications: mutateNotificationsType;
};

const ButtonMarkAsRead = ({ mutateNotifications }: ButtonMarkAsReadProp) => {
    const { t } = useTranslation();
    const [isSubmitting, setIsSubmitting] = useState(false);
    const { toast, toastError } = useToastsContext();
    const { mutateToastsNotifications } = useToastsNotifications({
        revalidateOnMount: false,
        shouldRetryOnError: false,
        revalidateOnFocus: false,
        revalidateOnReconnect: false
    });

    const cancelMarkAllAsRead = (rollbackNotifications: NotificationIdType[] = []) => {
        post(`${NOTIFICATIONS_MARK_AS_READ}/undo`, {
            notifications_ids: rollbackNotifications
        });
        mutateToastsNotifications(
            (prevState) => ({
                ...prevState,
                data: {
                    ...prevState.data,
                    total_unread: rollbackNotifications.length
                }
            }),
            false
        );
        mutateNotifications((prevState) => {
            return prevState.map((item) => {
                return {
                    ...item,
                    total_unread: rollbackNotifications.length,
                    notifications: item?.notifications?.map((notification) => {
                        if (rollbackNotifications.includes(notification.id)) {
                            return {
                                ...notification,
                                read: false
                            };
                        }

                        return notification;
                    })
                };
            });
        }, false);
    };

    const markAllAsRead = async () => {
        setIsSubmitting(true);
        try {
            const { data }: { data: NotificationIdType[] } = await post(NOTIFICATIONS_MARK_AS_READ);

            mutateToastsNotifications(
                (prevState) => ({
                    ...prevState,
                    data: {
                        ...prevState.data,
                        total_unread: 0
                    }
                }),
                false
            );

            mutateNotifications((prevState) => {
                return prevState.map((item) => {
                    return {
                        ...item,
                        total_unread: 0,
                        notifications: item?.notifications?.map((notification) => {
                            return {
                                ...notification,
                                read: true
                            };
                        })
                    };
                });
            }, false);

            // Launch undo toast
            toast(
                {
                    title: t('notifications:notification_marked_as_read'),
                    icon: 'notification'
                },
                {
                    cancelAction: () => cancelMarkAllAsRead(data)
                }
            );
            setIsSubmitting(false);
        } catch (e) {
            toastError({ title: t('notifications:error_mark_all_as_read') });
            setIsSubmitting(false);
        }
    };

    return (
        <button
            disabled={isSubmitting}
            className={clsx(
                'flex items-center space-x-2 text-xs text-icoBlue',
                'h-10 w-10 justify-center rounded-full bg-white dark:bg-icoDarkMode-deepBlack',
                'lg:h-auto lg:w-auto lg:rounded-none lg:bg-transparent lg:dark:bg-transparent'
            )}
            onClick={markAllAsRead}>
            {isSubmitting ? (
                <Loader className="h-3.5 w-3.5" />
            ) : (
                <Icon icon="check" className="text-sm" />
            )}
            <span className="hidden lg:inline-block">{t('notifications:mark_all_as_read')}</span>
        </button>
    );
};

export default ButtonMarkAsRead;
